import { instance } from "api/apiBase";
import type { OwnerScreeningReport } from "api/owner-screening-report";

export const QUERY_OWNER_REPORT_SHARES = (applicationId: string | number) => [
  "OWNER_REPORT_SHARES",
  String(applicationId)
];

export const QUERY_OWNER_REPORT_PREVIEW = (applicationId: string | number) => [
  "OWNER_REPORT_PREVIEW",
  String(applicationId)
];

export const MUTATION_OWNER_REPORT_SHARE_CREATE = [
  "OWNER_REPORT_SHARES",
  "CREATE"
];
export const MUTATION_OWNER_REPORT_SHARE_DELETE = [
  "OWNER_REPORT_SHARES",
  "DELETE"
];

/** Mirrors BoomScreen::Application::OwnerReport::Share::MAX_RECIPIENTS. */
export const MAX_OWNER_RECIPIENTS = 10;

export type OwnerShareState = "active" | "revoked" | "expired";

/**
 * One shared link. A share is one link however many owners it went to, so the
 * state, the view count and Revoke all apply to every address in `emails`.
 */
export interface OwnerReportShare {
  id: number;
  emails: string[];
  report_title: string;
  shared_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  state: OwnerShareState;
  /** Null once the link is dead — there is nothing useful left to copy. */
  url: string | null;
  shared_by: { id: number; name: string } | null;
  /**
   * This link went out with checks still running and they have since finished.
   * The payload is frozen when a link is sent, so the owner is still reading
   * "In progress" and will until somebody shares again.
   */
  reports_finished_since_share: boolean;
}

export interface OwnerSharePayload {
  emails: string[];
  message?: string;
  /** False for Copy link: mint the link and record the owners, email nobody. */
  notify?: boolean;
}

interface OwnerShareResult {
  share: OwnerReportShare;
  /** Named, so the dialog can say who is missing and why to share again later. */
  excluded_people: string[];
}

interface OwnerReportPreview {
  report: OwnerScreeningReport;
  excluded_people: string[];
}

const base = (applicationId: string | number) =>
  `/portal/v1/applications/${applicationId}/owner_report_shares`;

export const getOwnerReportShares = async (applicationId: string | number) => {
  const { data } = await instance.get<OwnerReportShare[]>(base(applicationId));
  return data;
};

export const getOwnerReportPreview = async (applicationId: string | number) => {
  const { data } = await instance.get<OwnerReportPreview>(
    `${base(applicationId)}/preview`
  );
  return data;
};

export const createOwnerReportShare = async (
  applicationId: string | number,
  payload: OwnerSharePayload
) => {
  const { data } = await instance.post<OwnerShareResult>(
    base(applicationId),
    payload
  );
  return data;
};

export const revokeOwnerReportShare = async (
  applicationId: string | number,
  shareId: number
) => {
  const { data } = await instance.delete<OwnerReportShare>(
    `${base(applicationId)}/${shareId}`
  );
  return data;
};
